import { useEffect, useState } from "react";
import { Tooltip, Whisper } from "rsuite";
import { Button } from "reactstrap";
import _ from "underscore";

import { getTextColor } from "../../utils/DashboardUtils";
import useGlobalLists from "../../api/api-hooks/cloud/useGlobalLists";
import useTenantUsers from "../../api/api-hooks/tenantUsers/useTenantUsers";
import Constants from "../../utils/Constants";
import StrUtils from "../../utils/StrUtils";
import useUser from "../../api/api-hooks/auth/useUser";
import useAllTags from "../../hooks/useAllTags";
import FieldUtils from "../../utils/FieldUtils";
import { buildDateFilterOptions } from "./QuickFilterUtils";
import { Field, Module } from "../../types/TenantInterfaces";

/**
 * Quick filter menu options
 */
interface QuickFilterMenuProps {
  currentModule: Module | null;
  existingQuery: any;
  onChange?: (query: any) => void;
  subitemFieldsOnly?: boolean;
}

type QuickFilterField = Field & {
  FilterOptions: FilterOption[];
};

type FilterOptionSourceList =
  | string
  | string[]
  | {
      Name: string;
      Value: string;
      Group?: string;
      Scope?: TagsScope;
      Color?: string;
    }[];

interface FilterOption {
  Field: {
    Name: string;
    APIName: string;
    FieldType: string;
    SourcesList?: FilterOptionSourceList;
  };
  CurrentUser?: boolean;
  Name: string;
  Value: any;
  Selected: boolean;
  id: string;
  Color?: string;
  Filter: {
    Field: string;
    Method: string;
    Value?: any;
    Color?: string;
    Scope?: TagsScope;
    Group?: string;
    Min?: string;
    Max?: string;
  };
}

type TagsScope =
  | { Type: "Cloud" }
  | { Type: "Person"; PersondID: string }
  | { Type: "Workspace"; WorkspaceID: string }
  | { Type: "Module"; ModuleAPIName: string };

const QuickFilterMenu = ({
  currentModule = null,
  existingQuery = null,
  onChange,
  subitemFieldsOnly,
}: QuickFilterMenuProps) => {
  const [quickFilterFields, setQuickFilterFields] = useState<
    QuickFilterField[]
  >([]);
  const [queryJSON, setQueryJSON] = useState("");
  const { users } = useTenantUsers();
  const { globalLists } = useGlobalLists();
  const { user } = useUser();
  const { tags: allTags } = useAllTags(currentModule?.APIName);

  /**
   * Gets the tag scope label
   */
  const renderTagScopeLabel = (scope: TagsScope) => {
    if (scope?.Type === "Cloud") {
      return "Cloud Tags";
    }

    if (scope?.Type === "Workspace") {
      return "Workspace Tags";
    }

    if (scope?.Type === "Person") {
      return "Your Tags";
    }

    if (scope?.Type === "Module") {
      return "Module Tags";
    }

    return "";
  };

  /**
   * Constructs all the permutations of a 2D array and returns the result.
   *
   * @param {*} list
   * @param {*} n
   * @param {*} result
   * @param {*} current
   * @returns
   */
  const buildQueryPermutations = (
    list: any[],
    n = 0,
    result: any[] = [],
    current: any[] = []
  ) => {
    if (n === list.length) result.push(current);
    else
      list[n].forEach((item: any) =>
        buildQueryPermutations(list, n + 1, result, [...current, item])
      );

    return result;
  };

  const tagSorting = (a: FilterOption, b: FilterOption) => {
    const aName = a.Name.toLowerCase();
    const bName = b.Name.toLowerCase();
    const aGroup = a.Filter?.Group?.toLowerCase() || "";
    const bGroup = b.Filter?.Group?.toLowerCase() || "";
    const aScopeType = a.Filter?.Scope?.Type?.toLowerCase() || null;
    const bScopeType = b.Filter?.Scope?.Type?.toLowerCase() || null;
    let aScopeValue = null;
    let bScopeValue = null;

    // Assign scope priority
    if (aScopeType === null) {
      aScopeValue = 0;
    } else if (aScopeType === "person") {
      aScopeValue = 1;
    } else if (aScopeType === "module") {
      aScopeValue = 2;
    } else if (aScopeType === "workspace") {
      aScopeValue = 3;
    } else if (aScopeType === "cloud") {
      aScopeValue = 4;
    } else {
      aScopeValue = 0;
    }

    if (bScopeType === null) {
      bScopeValue = 0;
    } else if (bScopeType === "person") {
      bScopeValue = 1;
    } else if (bScopeType === "module") {
      bScopeValue = 2;
    } else if (bScopeType === "workspace") {
      bScopeValue = 3;
    } else if (bScopeType === "cloud") {
      bScopeValue = 4;
    } else {
      bScopeValue = 0;
    }

    // Return value by priority
    if (aScopeValue > bScopeValue || (!bScopeValue && aScopeValue)) {
      return -1;
    }
    if (aScopeValue < bScopeValue || (!aScopeValue && bScopeValue)) {
      return 1;
    }
    if (aScopeValue === bScopeValue) {
      if (aGroup < bGroup || (!bGroup && aGroup)) {
        return -1;
      }
      if (aGroup > bGroup || (!aGroup && bGroup)) {
        return 1;
      }
      if (aGroup === bGroup) {
        if (aName < bName) {
          return -1;
        }
        if (aName > bName) {
          return 1;
        }
        return 0;
      }
      return 0;
    }

    return 0;
  };

  /**
   * Handles updating the query filters applied
   */
  const updateQuery = (filterFields: QuickFilterField[]) => {
    let newFields = JSON.parse(
      JSON.stringify(filterFields)
    ) as QuickFilterField[];

    newFields =
      _.filter(newFields, (field) => {
        return !!_.findWhere(field?.FilterOptions, { Selected: true });
      }) ?? [];

    for (let i = 0; i < newFields?.length; i++) {
      newFields[i].FilterOptions = _.filter(
        newFields[i].FilterOptions,
        (opt) => {
          return opt.Selected;
        }
      );
    }

    newFields = _.sortBy(newFields, (field) => {
      return field?.FilterOptions?.length;
    });

    newFields.reverse();

    let filters = [];

    for (let i = 0; i < newFields.length; i++) {
      filters.push(
        newFields[i].FilterOptions.map((f) => {
          return f.Filter;
        })
      );
    }

    let permutations = filters.length ? buildQueryPermutations(filters) : [];

    let filterGroups = permutations?.length
      ? permutations?.map((filters) => {
          return {
            Group: true,
            Filters: filters,
          };
        })
      : [];

    let newQuery = JSON.parse(JSON.stringify(existingQuery || {}));

    newQuery.Filters = filterGroups;

    setQueryJSON(JSON.stringify(newQuery));

    if (typeof onChange == "function") {
      onChange(newQuery);
    }
  };

  const renderName = (option: FilterOption) => {
    if (option?.Field?.FieldType === Constants.FIELDTYPES.TAGS) {
      return (
        <div>
          <Whisper
            placement="auto"
            trigger="hover"
            delayOpen={400}
            speaker={
              <Tooltip>
                {option?.Filter?.Scope && (
                  <div className="small">
                    {renderTagScopeLabel(option?.Filter?.Scope)}
                  </div>
                )}
                <div className="text-truncate">
                  {option?.Filter?.Group ? `${option?.Filter?.Group}: ` : ""}
                  {option.Name}
                </div>
              </Tooltip>
            }
          >
            <h5
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              className="m-0 font-weight-500 d-flex justify-content-start align-items-center"
            >
              <div>
                <i
                  className={`mdi ${
                    option?.Selected ? "mdi-tag" : "mdi-tag-outline"
                  } mr-2`}
                  style={{
                    position: "relative",
                    top: 1,
                    lineHeight: 1,
                    fontSize: 16,
                  }}
                ></i>
              </div>
              <div>
                {option?.Filter?.Scope && (
                  <div
                    className="small"
                    style={{
                      ...(option?.Color && {
                        color: option.Selected
                          ? "#000000"
                          : getTextColor(option?.Color),
                      }),
                    }}
                  >
                    {renderTagScopeLabel(option?.Filter?.Scope)}
                  </div>
                )}
                <div
                  style={{
                    ...(option?.Color && {
                      color: option.Selected
                        ? "#000000"
                        : getTextColor(option?.Color),
                    }),
                  }}
                >
                  {option?.Filter?.Group ? `${option?.Filter?.Group}: ` : ""}
                  {option.Name}
                </div>
              </div>
            </h5>
          </Whisper>
        </div>
      );
    }

    if (
      (option?.Field?.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE ||
        option?.Field?.FieldType === Constants.FIELDTYPES.MULTI_REFERENCE) &&
      (option?.Field?.SourcesList === "Tenant.Modules.Contacts" ||
        option?.Field?.SourcesList === "Any")
    ) {
      return (
        <div style={{ position: "relative", height: 19 }}>
          <div
            style={{
              position: "absolute",
              borderRadius: 100,
              height: 27,
              width: 27,
              top: -4,
              left: -4,
              lineHeight: 1,
              textAlign: "center",
              paddingTop: 7,
              fontSize: 11,
              paddingLeft: 1,
            }}
            className="d-inline-block mr-2 border border-superlight font-weight-500 bg-white"
          >
            {option?.CurrentUser ? (
              <>
                <i
                  className={`mdi ${
                    option?.Selected ? "mdi-account" : "mdi-account-outline"
                  }`}
                  style={{
                    fontSize: 16,
                    lineHeight: 1,
                    position: "relative",
                    top: -3,
                  }}
                ></i>
              </>
            ) : (
              <>{StrUtils.getInitials(option?.Name)}</>
            )}
          </div>
          <h5
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              position: "relative",
              left: 30,
              top: 0,
              width: `calc(100% - ${option?.Selected ? 20 : 30}px)`,
            }}
            className="m-0 font-weight-500 d-inline-block"
          >
            {option.Name}
          </h5>
        </div>
      );
    }

    if (
      option?.Field?.FieldType === Constants.FIELDTYPES.DATE ||
      option?.Field?.FieldType === Constants.FIELDTYPES.DATE_TIME
    ) {
      return (
        <h5
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          className="m-0 font-weight-500"
        >
          <i
            className={`mdi ${
              option?.Selected ? "mdi-calendar-check" : "mdi-calendar"
            } mr-2`}
            style={{
              position: "relative",
              top: 2,
              lineHeight: 1,
              fontSize: 16,
            }}
          ></i>
          {option.Name}
        </h5>
      );
    }

    return (
      <h5
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...(option?.Color && {
            color: option.Selected ? "#000000" : getTextColor(option?.Color),
          }),
        }}
        className="m-0 font-weight-500"
      >
        {option.Name}
      </h5>
    );
  };

  /**
   * Loads the quick filter options based on the provided module schema
   */
  const loadQuickFilters = async (
    currentModule: Module | null,
    query: any = null
  ) => {
    if (!currentModule) return [];

    if (!FieldUtils.getFieldsFromModuleOrSubitem(currentModule)?.length) {
      return [];
    }

    let newQuery = JSON.parse(JSON.stringify(query));

    // Update the query json
    if (JSON.stringify(newQuery) !== queryJSON) {
      setQueryJSON(JSON.stringify(newQuery));

      // TODO: add checker for if a query can be converted
    }

    let existingFilters: any[] = [];

    for (let i = 0; i < newQuery?.Filters?.length; i++) {
      if (!newQuery?.Filters[i]?.Group) {
        continue;
      }

      existingFilters = existingFilters.concat(newQuery?.Filters[i]?.Filters);
    }

    const getSubitemFields = (): Field[] => {
      const subitemField = _.findWhere(
        FieldUtils.getFieldsFromModuleOrSubitem(currentModule),
        {
          APIName: subitemFieldsOnly,
        }
      );

      return subitemField?.SubitemFields.map((f: Field) => ({
        ...f,
        APIName: `${subitemFieldsOnly}.${f.APIName}`,
      }));
    };

    let allFields = subitemFieldsOnly
      ? getSubitemFields()
      : FieldUtils.getFieldsFromModuleOrSubitem(currentModule);

    let fields = [];
    let filterableTypes = [
      Constants.FIELDTYPES.SINGLE_SELECT,
      Constants.FIELDTYPES.MULTI_SELECT,
      Constants.FIELDTYPES.TAGS,
      Constants.FIELDTYPES.DATE,
      Constants.FIELDTYPES.DATE_TIME,
      Constants.FIELDTYPES.SINGLE_REFERENCE,
      Constants.FIELDTYPES.MULTI_REFERENCE,
    ];

    for (let i = 0; i < allFields.length; i++) {
      let field = _.clone(allFields[i]);

      if (!filterableTypes.includes(field?.FieldType)) {
        continue;
      }

      if (
        (field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE ||
          field.FieldType === Constants.FIELDTYPES.MULTI_REFERENCE) &&
        field.SourcesList !== "Tenant.Modules.Contacts" &&
        field.SourcesList !== "Any" &&
        field.SourcesList?.indexOf("Global.Lists") < 0
      ) {
        continue;
      }

      let filterOptions: FilterOption[] = [];

      // Global Lists
      if (field?.SourcesList?.includes("Global.Lists.") && globalLists) {
        const listName: string = field.SourcesList.replace("Global.Lists.", "");

        const list = _.findWhere(globalLists, { APIName: listName });

        if (!list?.Variables) continue;

        field.SourcesList = list.Variables.map((l: any) => ({
          Name: l.Name,
          Value: l.Value,
        }));

        const sortedOptions = _.sortBy(field?.SourcesList, "Name");

        filterOptions = sortedOptions?.map((opt) => {
          return {
            Field: {
              APIName: field?.APIName,
              FieldType: field?.FieldType,
              Name: field?.Name,
              SourcesList: field?.SourcesList,
            },
            Name: opt.Name,
            Value: opt.Value,
            Selected: _.findWhere(existingFilters, {
              Field: field.APIName,
              Method: "equals",
              Value: opt.Value,
            })
              ? true
              : false,
            id: StrUtils.uuidv4(),
            Color: opt?.Color,
            Filter: {
              Field: field.APIName,
              Method: "equals",
              Value: opt.Value,
              Color: opt?.Color,
              Scope: opt?.Scope,
              Group: opt?.Group,
            },
          };
        });
      }

      if (
        field.FieldType === Constants.FIELDTYPES.DATE ||
        field.FieldType === Constants.FIELDTYPES.DATE_TIME
      ) {
        filterOptions = buildDateFilterOptions(field);

        for (let i = 0; i < filterOptions.length; i++) {
          let selected = false;

          if (filterOptions[i]?.Filter?.Method === "between") {
            selected = _.findWhere(existingFilters, {
              Field: filterOptions[i].Filter.Field,
              Method: filterOptions[i].Filter.Method,
              Min: filterOptions[i].Filter.Min,
              Max: filterOptions[i].Filter.Max,
            })
              ? true
              : false;
          } else {
            selected = _.findWhere(existingFilters, {
              Field: filterOptions[i].Filter.Field,
              Method: filterOptions[i].Filter.Method,
            })
              ? true
              : false;
          }

          filterOptions[i].Selected = selected;
        }
      }

      if (
        field.FieldType === Constants.FIELDTYPES.SINGLE_SELECT ||
        field.FieldType === Constants.FIELDTYPES.MULTI_SELECT ||
        field.FieldType === Constants.FIELDTYPES.TAGS
      ) {
        if (field.FieldType === Constants.FIELDTYPES.TAGS) {
          field.SourcesList = allTags?.map((tag: any) => ({
            Name: tag.Name,
            Value: tag.id,
            Group: tag?.TagGroup,
            Scope: tag?.Scope,
            Color: tag?.Color,
          }));
        }

        const sortedOptions = _.sortBy(field?.SourcesList, "Name");

        filterOptions = sortedOptions?.map((opt) => {
          return {
            Field: {
              APIName: field?.APIName,
              FieldType: field?.FieldType,
              Name: field?.Name,
            },
            Name: opt.Name,
            Value: opt.Value,
            Selected: (existingFilters ?? []).find((existingFilter) => {
              const matchValue = Array.isArray(existingFilter.Value)
                ? existingFilter.Value.includes(opt.Value)
                : existingFilter.Value === opt.Value;

              if (
                existingFilter.Field === field.APIName &&
                existingFilter.Method ===
                  (field?.FieldType === Constants.FIELDTYPES.SINGLE_SELECT
                    ? "equals"
                    : "contains") &&
                matchValue
              ) {
                return true;
              }

              return false;
            })
              ? true
              : false,
            id: StrUtils.uuidv4(),
            Color: opt?.Color,
            Filter: {
              Field: field.APIName,
              Method:
                field.FieldType === Constants.FIELDTYPES.SINGLE_SELECT
                  ? "equals"
                  : "contains",
              Value:
                field.FieldType === Constants.FIELDTYPES.SINGLE_SELECT
                  ? opt.Value
                  : [opt.Value],
              Color: opt?.Color,
              Scope: opt?.Scope,
              Group: opt?.Group,
            },
          };
        });
      }

      if (
        (field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE ||
          field.FieldType === Constants.FIELDTYPES.MULTI_REFERENCE) &&
        (field.SourcesList === "Tenant.Modules.Contacts" ||
          field.SourcesList === "Any")
      ) {
        if (!users) {
          filterOptions = [];
        } else {
          let newUsers = _.sortBy(users, "Name");

          newUsers = _.filter(users, (u) => {
            return u?.id !== user?.id;
          });

          let opts = newUsers?.map((member) => {
            return {
              Field: {
                APIName: field?.APIName,
                FieldType: field?.FieldType,
                Name: field?.Name,
                SourcesList: field?.SourcesList,
              },
              Name: member?.Name,
              Selected: existingFilters.find((existingFilter) => {
                const matchValue = Array.isArray(existingFilter.Value)
                  ? existingFilter.Value.find((r: any) => r.id === member?.id)
                  : existingFilter.Value.id === member?.id;

                if (
                  existingFilter.Field === field.APIName &&
                  existingFilter.Method ===
                    (Constants.FIELDTYPES.SINGLE_REFERENCE === field?.FieldType
                      ? "equals"
                      : "contains") &&
                  matchValue
                ) {
                  return true;
                }

                return false;
              })
                ? true
                : false,
              Value:
                field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
                  ? {
                      ModuleAPIName: "Contacts",
                      id: member?.id,
                      _RecordName: member?.Name,
                    }
                  : [
                      {
                        ModuleAPIName: "Contacts",
                        id: member?.id,
                        _RecordName: member?.Name,
                      },
                    ],
              id: StrUtils.uuidv4(),
              Filter: {
                Field: field.APIName,
                Method:
                  field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
                    ? "equals"
                    : "contains",
                Value:
                  field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
                    ? {
                        ModuleAPIName: "Contacts",
                        id: member?.id,
                        _RecordName: member?.Name,
                      }
                    : [
                        {
                          ModuleAPIName: "Contacts",
                          id: member?.id,
                          _RecordName: member?.Name,
                        },
                      ],
              },
            };
          });

          filterOptions = [
            {
              Field: {
                APIName: field?.APIName,
                FieldType: field?.FieldType,
                Name: field?.Name,
                SourcesList: field?.SourcesList,
              },
              CurrentUser: true,
              Name:
                field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
                  ? "Me"
                  : "Includes Me",
              Selected: existingFilters.find((existingFilter) => {
                const matchValue = !!(Array.isArray(existingFilter.Value)
                  ? existingFilter.Value.find((r: any) => r.id === user?.id)
                  : existingFilter.Value.id === user?.id);

                if (
                  existingFilter.Field === field.APIName &&
                  existingFilter.Method ===
                    (Constants.FIELDTYPES.SINGLE_REFERENCE === field?.FieldType
                      ? "equals"
                      : "contains") &&
                  matchValue
                ) {
                  return true;
                }

                return false;
              })
                ? true
                : false,
              Value:
                field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
                  ? {
                      ModuleAPIName: "Contacts",
                      id: user?.id,
                      _RecordName: user?.Name,
                    }
                  : [
                      {
                        ModuleAPIName: "Contacts",
                        id: user?.id,
                        _RecordName: user?.Name,
                      },
                    ],
              id: StrUtils.uuidv4(),
              Filter: {
                Field: field.APIName,
                Method:
                  field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
                    ? "equals"
                    : "contains",
                Value:
                  field.FieldType === Constants.FIELDTYPES.SINGLE_REFERENCE
                    ? {
                        ModuleAPIName: "Contacts",
                        id: user?.id,
                        _RecordName: user?.Name,
                      }
                    : [
                        {
                          ModuleAPIName: "Contacts",
                          id: user?.id,
                          _RecordName: user?.Name,
                        },
                      ],
              },
            },
          ];

          filterOptions = filterOptions.concat(opts);
        }
      }

      field.FilterOptions = filterOptions;

      if (field?.FilterOptions?.length) {
        fields.push(field);
      }
    }

    setQuickFilterFields(fields);
  };

  /**
   * Toggles a quick filter option.
   */
  const toggleOption = (field: QuickFilterField, option: FilterOption) => {
    if (!field || !option) {
      return;
    }

    let newQuickFilterFields = JSON.parse(
      JSON.stringify(quickFilterFields)
    ) as QuickFilterField[];

    let fieldIdx = _.findIndex(newQuickFilterFields, {
      APIName: field?.APIName,
    });

    if (fieldIdx < 0) {
      return;
    }

    let optionIdx = _.findIndex(newQuickFilterFields[fieldIdx].FilterOptions, {
      id: option?.id,
    });

    if (optionIdx < 0) {
      return;
    }

    newQuickFilterFields[fieldIdx].FilterOptions[optionIdx].Selected =
      !newQuickFilterFields[fieldIdx]?.FilterOptions[optionIdx]?.Selected;

    // TODO: send on query change here.

    setQuickFilterFields(newQuickFilterFields);

    updateQuery(newQuickFilterFields);
  };

  useEffect(() => {
    loadQuickFilters(currentModule, existingQuery);
  }, [currentModule, existingQuery, users]);

  return (
    <>
      <div className="px-2 pt-2 pb-0" style={{ minHeight: "250px" }}>
        <div className="mb-2">
          <h5 className="text-dark font-weight-500">All Fields</h5>
        </div>
        <div
          style={{
            whiteSpace: "nowrap",
            overflowX: "scroll",
            overflowY: "hidden",
          }}
        >
          {quickFilterFields.map((field, i) => {
            let sortedFilterOptions = [];
            if (field.APIName === "Tags") {
              sortedFilterOptions = field.FilterOptions.sort(tagSorting);
            } else {
              sortedFilterOptions = field?.FilterOptions;
            }

            return (
              <div
                key={i}
                className={`d-inline-block border border-superlight mb-2 ${
                  i !== quickFilterFields.length - 1 ? "mr-3" : ""
                }`}
                style={{
                  width: "200px",
                  height: "250px",
                  overflow: "hidden",
                  borderRadius: 4,
                }}
              >
                <div
                  className="p-2"
                  style={{
                    borderBottom: "1px solid #e3e7e4",
                    background: "#f0f3f2",
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <h5 className="text-dark font-weight-500 mb-0">
                    {field.Name}
                  </h5>
                </div>
                <div
                  className="p-2"
                  style={{
                    height: "213px",
                    overflowX: "hidden",
                    overflowY: "scroll",
                  }}
                >
                  {sortedFilterOptions.map((option, k) => (
                    <div
                      onClick={() => {
                        toggleOption(field, option);
                      }}
                      className={`p-1 text-dark cursor-pointer ${
                        option?.Selected
                          ? "bg-dropdown-selected border-dropdown-selected-dark bg-dropdown-selected-dark--hover border-primary--hover "
                          : `${
                              !option?.Color ? "bg-ultralight" : ""
                            } border-superlight border-superlight--hover ${
                              !option?.Color ? "bg-dropdown-hover--hover" : "" // TODO: Modify the hover color.
                            } `
                      } cursor-pointer border ${
                        k !== sortedFilterOptions?.length - 1 ? "mb-2" : "mb-0"
                      }`}
                      style={{
                        borderRadius: 4,
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        position: "relative",
                        ...(option?.Color && {
                          backgroundColor: option?.Color,
                        }),
                      }}
                      key={k}
                    >
                      <div
                        className="p-1"
                        style={{
                          width: option?.Selected ? 140 : 172,
                        }}
                      >
                        {renderName(option)}
                      </div>
                      {option?.Selected && (
                        <div
                          className="d-inline-block"
                          style={{
                            position: "absolute",
                            right: "0.25rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <Button
                            outline
                            size="sm"
                            className="btn-icon-only"
                            color="secondary"
                            style={{
                              width: "1.5rem",
                              height: "1.5rem",
                            }}
                          >
                            <i
                              style={{ fontSize: 20 }}
                              className="mdi mdi-check"
                            ></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default QuickFilterMenu;
